import { defineComponent as _defineComponent } from 'vue'
import CollapsableCard from "@/components/bootstrap/CollapsableCard.vue";
import {useTranslations} from "@/hooks/useTranslations";
import {computed, ref} from "vue";
import {FOPLabelFormatData} from "@/models/NutritionLabels/FOPLabelFormatData";
import {SupportedPDS} from "@/models/NutritionLabels/SupportedPDS";
import {CanadaFOPHighInMetadata} from "@/models/NutritionLabels/CanadaFOPHighInMetadata";
import NutritionLabelRepository from "@/utils/API/NutritionLabelRepository";
import {LabelLanguage} from "@/models/NutritionLabels/LabelLanguage";
import {NutritionalFrontOfPackRenderData} from "@/models/NutritionLabels/ExportLabelState";
import VUpsellModal from "@/components/modals/marketing/vUpsellModal.vue";
import { VPopover } from "@/components/v-tooltip";

type CanadaFrontOfPackConfigurationProps = {
  language: string;
  hasCautionary: boolean;
  highIn: CanadaFOPHighInMetadata;
  isTrial?: boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CanadaFrontOfPackConfiguration',
  props: {
    language: null,
    hasCautionary: { type: Boolean },
    highIn: null,
    isTrial: { type: Boolean }
  },
  emits: ["update:renderModel"],
  setup(__props: any, { expose, emit }) {

const props = __props as {
  language: string;
  hasCautionary: boolean;
  highIn: CanadaFOPHighInMetadata;
  isTrial?: boolean;
};




const translations = useTranslations();

const selectedFormat = ref<FOPLabelFormatData>({
  formatValue: 1,
  language: LabelLanguage.English,
  supportedPDS: [],
  formatTranslationKey: ""
});
const selectedPDS = ref<SupportedPDS>({ translationKey: "", value: 1 });
const fopData = ref<NutritionalFrontOfPackRenderData>({
  fopFormat: 1,
  pdsSize: 1,
  includeInDownload: true
});
const pdsSizeOptions = ref<SupportedPDS[]>([]);
const labelFormatOptions = ref<FOPLabelFormatData[]>([]);
const trialDefaultFormat = ref<number | null>(null);
const trialDefaultPds = ref<number | null>(null);
const showTrialUpsellModal = ref(false);

const labelFormatOptionsForTrial = computed(() => {
  if (!props.isTrial) {
    return labelFormatOptions.value;
  }

  const fallbackDefault = labelFormatOptions.value[0]?.formatValue;
  const defaultFormat = trialDefaultFormat.value ?? fallbackDefault;

  return labelFormatOptions.value.map((option) => ({
    ...option,
    locked: option.formatValue !== defaultFormat,
  }));
});

const pdsSizeOptionsForTrial = computed(() => {
  if (!props.isTrial) {
    return pdsSizeOptions.value;
  }

  const fallbackDefault = pdsSizeOptions.value[0]?.value;
  const defaultPds = trialDefaultPds.value ?? fallbackDefault;

  return pdsSizeOptions.value.map((option) => ({
    ...option,
    locked: option.value !== defaultPds,
  }));
});

const renderModel = computed(() => {
  return {
    country: "Canada",
    labelName: "NutritionalFrontOfPack",
    renderModel: {
      format: selectedFormat.value.formatValue,
      pdsSize: selectedPDS.value.value,
      highIn: props.highIn
    }
  };
});

function loadLabelFormats() {
  // @ts-expect-error TEMP For Demo
  NutritionLabelRepository.getFrontOfPackageOptions(LabelLanguage[props.language]).then((response) => {
    labelFormatOptions.value = response.data;
    let savedFormat = labelFormatOptions.value.find(
      (x: any) => x.formatValue == fopData.value.fopFormat
    );

    updateFOPSelections(savedFormat || labelFormatOptions.value[0]);
  });
}

function updateFOPSelections(chosenFormat: FOPLabelFormatData){
  selectedFormat.value = chosenFormat;
  pdsSizeOptions.value = chosenFormat.supportedPDS;

  let selectedPDSFormat = pdsSizeOptions.value.find(
    (x: any) => x.value == fopData.value.pdsSize
  );

  if(selectedPDSFormat) {
    selectedPDS.value = selectedPDSFormat;
  }

  else {
    selectedPDS.value = pdsSizeOptions.value[0];
  }

  if (props.isTrial) {
    trialDefaultFormat.value = selectedFormat.value?.formatValue ?? null;
    trialDefaultPds.value = selectedPDS.value?.value ?? null;
  }

  emit("update:renderModel");
}

function fetchLabelFormats(chosenFormat: FOPLabelFormatData) {
  if (props.isTrial && chosenFormat?.formatValue !== trialDefaultFormat.value) {
    return;
  }

  const selected = labelFormatOptions.value.find(
    (format) => format.formatValue === chosenFormat.formatValue
  );

  if (selected) {
    selectedFormat.value = selected;
    fopData.value.fopFormat = selected.formatValue;
    updatePDSSizes(selected.supportedPDS);
  }

  emit("update:renderModel");
}

function handlePdsChange(chosenPds: SupportedPDS) {
  if (props.isTrial && chosenPds?.value !== trialDefaultPds.value) {
    return;
  }

  const selected = pdsSizeOptions.value.find((option) => option.value === chosenPds.value);
  if (!selected) {
    return;
  }

  selectedPDS.value = selected;
  fopData.value.pdsSize = selected.value;
  emit("update:renderModel");
}

function handleFormatOptionPointerDown(locked: boolean, event: MouseEvent) {
  if (!locked) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
}

function handlePdsOptionPointerDown(locked: boolean, event: MouseEvent) {
  if (!locked) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
}

function closeTrialUpsellModal() {
  showTrialUpsellModal.value = false;
}

function handleTrialCardExpandAttempt(event: MouseEvent) {
  if (!props.isTrial) {
    return;
  }

  const targetElement = event.target as HTMLElement | null;
  const cardHeader = targetElement?.closest(".card-title") as HTMLElement | null;

  if (!cardHeader) {
    return;
  }

  const chevron = cardHeader.querySelector(".chevron") as HTMLElement | null;
  const isCollapsed = !!chevron && !chevron.classList.contains("active");

  if (!isCollapsed) {
    return;
  }

  showTrialUpsellModal.value = true;
}

function updatePDSSizes(supportedPDS: SupportedPDS[]) {
  pdsSizeOptions.value = supportedPDS;

  selectedPDS.value = pdsSizeOptions.value[0];
  fopData.value.pdsSize = selectedPDS.value.value;
}

expose({
  renderModel,
  loadLabelFormats
});

return { __sfc: true,props, emit, translations, selectedFormat, selectedPDS, fopData, pdsSizeOptions, labelFormatOptions, trialDefaultFormat, trialDefaultPds, showTrialUpsellModal, labelFormatOptionsForTrial, pdsSizeOptionsForTrial, renderModel, loadLabelFormats, updateFOPSelections, fetchLabelFormats, handlePdsChange, handleFormatOptionPointerDown, handlePdsOptionPointerDown, closeTrialUpsellModal, handleTrialCardExpandAttempt, updatePDSSizes, CollapsableCard, VUpsellModal, VPopover }
}

})